import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { AddressRecord } from '@navio/nsdk';
import { noddiAsync, URLKeys } from 'noddi-async';
import { BOOKING_AVAILABLE_COUNTRY_CODES, BookingAddress } from 'noddi-types';
import { NoddiLink, usePaginationQueryParam } from 'noddi-ui-web';
import { customerRoutes } from 'noddi-util';
import {
  AddressRecoveryForm,
  AddressSearch,
  getLongAddressName,
  tracking,
  useAddressResolution,
  useAuthContext,
  useShouldFireRequestForLoggedInUser
} from 'noddi-web';
import { useCurrentBrand } from '../../stores/BrandStore/brandStore';
import { getCommonTranslations } from '../../utils/commonTranslations';
import { ApiErrorMessageWithTrans } from '../Error/ApiErrorMessageWithTrans';
import { AddressError } from './AddressError';
import { BookingDeliveryAnnouncement } from './BookingDeliveryAnnouncement';
import { NoServiceOffered } from './NoServiceOffered';
import { SavedAddressList } from './SavedAddressList';

const initialPageSize = 5;

type Props = {
  setSelectedAddress: (address?: BookingAddress) => void;
  isLoading?: boolean;
  selectedAddress?: BookingAddress | AddressRecord;
  onAddressCreationSuccess?: (bookingAddress: BookingAddress | AddressRecord) => void;
  /** Saved-card and saved-dropdown picks. Google search does not call this. */
  onSavedAddressSelected?: (address: BookingAddress) => void;
  /**
   * Group whose saved addresses to offer. Defaults to the signed-in group.
   * Proposal flows pass the proposal customer so a leftover proposal group is
   * not read from the booking store by every other caller.
   */
  userGroupId?: number;
  hideServiceAreaCheck?: boolean;
  /**
   * Passed to `AddressSearch`. On by default: every address step renders this picker as the first
   * field on the page. Turn off inside a dialog, where the search cannot scroll to the top.
   */
  keepPopoverBelowOnPhones?: boolean;
};

/**
 * Customer mobile booking address: Places search with brand-scoped delivery-area gate.
 * Owns booking policy (tracking, NoServiceOffered, delivery map) — not shared AddressPicker.
 * Service addresses require verified geocoded coordinates.
 *
 * Saved addresses are listed below the search. Picking one does not call Places.
 */
export const BookingAddressPicker = ({
  setSelectedAddress,
  isLoading,
  onAddressCreationSuccess,
  onSavedAddressSelected,
  selectedAddress,
  userGroupId: userGroupIdProp,
  hideServiceAreaCheck = false,
  keepPopoverBelowOnPhones = true
}: Props) => {
  const { currentBrand } = useCurrentBrand();
  const { currentUserGroupId } = useAuthContext();
  const userGroupId = userGroupIdProp ?? currentUserGroupId;
  const shouldFireRequest = useShouldFireRequestForLoggedInUser() && userGroupId != null;
  const { pagination, setPagination } = usePaginationQueryParam({
    initialPageSize,
    paramKeyPrefix: 'saved-addresses'
  });

  const {
    data: savedAddresses,
    isLoading: isLoadingSavedAddresses,
    error: savedAddressesError
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedUserGroupAddressesValidForNewBooking,
    input: { userGroupId: userGroupId!, brandDomain: currentBrand, ...pagination },
    queryConfig: { enabled: shouldFireRequest, staleTime: Infinity }
  });

  const savedResults = savedAddresses?.results ?? [];
  const savedAddressNames = savedResults.map((address) => getLongAddressName(address));

  const {
    resolve: createAddressFromPlaceId,
    recoveryFormProps,
    isPending,
    error: addressError,
    data: createdAddress,
    clear: resetCreateAddressFromPlaceId
  } = useAddressResolution({
    onSuccess: ({ isInDeliveryArea, ...rest }) => {
      if (isInDeliveryArea) {
        setSelectedAddress(rest);
        onAddressCreationSuccess?.(rest);
        return;
      }
      tracking.track('noServiceOffered', rest);
      setSelectedAddress(undefined);
    },
    onError: () => {
      setSelectedAddress(undefined);
    }
  });

  const selectSavedAddress = (address: BookingAddress) => {
    resetCreateAddressFromPlaceId();
    setSelectedAddress(address);
    onSavedAddressSelected?.(address);
  };

  const showLoader = isPending || Boolean(isLoading);
  const resolved = createdAddress?.data;
  const isAddressOutOfDeliveryArea = Boolean(resolved && !resolved.isInDeliveryArea);
  const outOfAreaAddress = isAddressOutOfDeliveryArea ? resolved : undefined;

  return (
    <div className='flex flex-col gap-4'>
      <AddressSearch
        countryCodes={BOOKING_AVAILABLE_COUNTRY_CODES}
        translations={getCommonTranslations().addressSearch}
        savedAddressNames={savedAddressNames}
        keepPopoverBelowOnPhones={keepPopoverBelowOnPhones}
        onSelect={(addressSuggestion) => {
          setSelectedAddress(undefined);
          createAddressFromPlaceId({
            placeId: addressSuggestion.placeId,
            brandDomains: [currentBrand]
          });
        }}
        defaultValue={selectedAddress}
        onClear={() => {
          setSelectedAddress(undefined);
          resetCreateAddressFromPlaceId();
        }}
        loading={showLoader}
      />
      {savedAddressesError && <ApiErrorMessageWithTrans error={savedAddressesError} />}
      {!selectedAddress && (
        <SavedAddressList
          items={savedResults.map((address) => ({
            id: address.id,
            addressName: getLongAddressName(address)
          }))}
          onSelect={(id) => {
            const savedAddress = savedResults.find((address) => address.id === id);
            if (savedAddress) {
              selectSavedAddress(savedAddress);
            }
          }}
          pagination={{
            pagination,
            loading: isLoadingSavedAddresses,
            totalCount: savedAddresses?.count ?? 0,
            onChange: ({ pageIndex, pageSize }) => setPagination({ pageIndex, pageSize, search: '' })
          }}
        />
      )}
      {selectedAddress && !isAddressOutOfDeliveryArea && !hideServiceAreaCheck && <BookingDeliveryAnnouncement />}
      {recoveryFormProps ? (
        <AddressRecoveryForm {...recoveryFormProps} translations={getCommonTranslations().addressSearch.recovery} />
      ) : (
        <AddressError addressError={addressError} />
      )}
      {outOfAreaAddress && (
        <>
          <NoServiceOffered
            translations={{
              noServiceOfferedNotice: getCommonTranslations().noServiceOfferedNotice
            }}
            address={outOfAreaAddress}
          />
          <NoddiLink startIcon='Map' target='_blank' to={customerRoutes.deliveryMap.getPath()}>
            <Trans>See map of where we currently deliver</Trans>
            <span className='sr-only'> ({t`opens in a new tab`})</span>
          </NoddiLink>
        </>
      )}
    </div>
  );
};
